import {ColumnProps} from "antd/lib/table";
import React, {FC, memo, useEffect, useState} from "react";
import {Button, message, Space, Table, Tooltip} from "antd";
import {ExpandableConfig} from "antd/lib/table/interface";
import {ExclamationCircleOutlined} from "@ant-design/icons";
import {red} from "@ant-design/colors";
import {
    Booker,
    Booking,
    GetAppSettingsDocument,
    GetAppSettingsQuery,
    GetBookedAttendeesByCourseIdDocument,
    GetBookingByIdDocument,
    GetBookingByIdWithCourseLessonBookingsDocument,
    GetPaymentMethodsByBookerIdDocument,
    GetPaymentMethodsByBookerIdQuery,
    SepaDebitIntput,
    useAddPaymentMethodMutation,
    useGetAppSettingsQuery,
    useGetPaymentMethodsByBookerIdQuery,
} from "../../generated/graphql";
import {getPaymentMethodType, PaymentMethodType} from "../../helpers/getPaymentMethodType";
import {getProviderForPaymentMethodType} from "../../helpers/paymentMethodProvider";
import PaymentMethodBadge from "../PaymentMethodBadge/PaymentMethodBadge";
import SepaDebitPaymentMethodModal from "../../containers/customers/paymentMethodForms/SepaDebitPaymentMethodModal";
import {useStaffPaymentMethodRestriction} from "../../hooks/useStaffPaymentMethodRestriction";
import i18n from "../../services/i18n";

type BookingAddPaymentMethodProps = {
    bookerId?: Booker["id"];
    courseId?: string;
    setSelectedPayment: Function;
    setModalOpen: Function;
    bookingId?: Booking["id"];
    okButtonText?: string;
};

const BookingAddPaymentMethod: FC<BookingAddPaymentMethodProps> = ({
    bookerId,
    courseId,
    setSelectedPayment,
    setModalOpen,
    bookingId,
    okButtonText,
}) => {
    // GET PAYMENTMETHODS OF COMPANY -----------------------------------------------------------------------------
    type CompanySettings = GetAppSettingsQuery["settings"];
    type CompanyPaymentMethod = "cash" | "esr" | "bankTransfer" | "sepaDebit";
    type CompanyMethods = Pick<CompanySettings, CompanyPaymentMethod | "bankTransferProvider" | "sepaDebitProvider">;
    const companyPaymentMethodKeys: Array<CompanyPaymentMethod> = ["cash", "esr", "bankTransfer", "sepaDebit"];

    const [companyPaymentMethods, setCompanyPaymentMethods] = useState<CompanyMethods>();

    const {data: companySettingsData} = useGetAppSettingsQuery();

    // Mitarbeiter-Sperre (ADR 0013, Issue #1006). Ohne den Tenant-Schalter liefert
    // `isTypeSelectable` immer true ⇒ unveränderte Auswahl wie bisher.
    const staffRestriction = useStaffPaymentMethodRestriction();

    useEffect(() => {
        if (companySettingsData) {
            // setCompanySettings(companySettingsData.settings)

            const companyPamentMethods = {
                cash: companySettingsData.settings.cash,
                esr: companySettingsData.settings.esr,
                bankTransfer: companySettingsData.settings.bankTransfer,
                sepaDebit: companySettingsData.settings.sepaDebit,
                bankTransferProvider: companySettingsData.settings.bankTransferProvider,
                sepaDebitProvider: companySettingsData.settings.sepaDebitProvider,
            };

            setCompanyPaymentMethods(companyPamentMethods);
        }
    }, [companySettingsData]);

    // GET PAYMENTMETHODS OF BOOKER ------------------------------------------------------------------------------

    const {data: bookerData} = useGetPaymentMethodsByBookerIdQuery({
        skip: !bookerId,
        variables: {bookerId: bookerId ?? ""},
        fetchPolicy: "network-only",
    });

    const bookerPaymentMethods: GetPaymentMethodsByBookerIdQuery["getPaymentMethodsByBookerId"] | undefined =
        bookerData?.getPaymentMethodsByBookerId;

    const bookerPaymentMethodTypes = bookerPaymentMethods?.map((bookerMethod) => {
        return getPaymentMethodType(bookerMethod);
    });

    // Alle Zahlarten, die der Bucher noch nicht hat. Ohne Mitarbeiter-Sperre bewusst AUCH die in den
    // Einstellungen nicht aktivierten (seit 0.9.5, 02/2023): Mitarbeiter dürfen sie im Einzelfall
    // anlegen, die Tabelle markiert sie mit dem Hinweis „notCompanyMethod“ (siehe isCompanyMethod).
    // Mit aktiver Mitarbeiter-Sperre (ADR 0013) fallen nicht freigegebene Arten weg — es soll gar
    // nicht erst eine gesperrte Methode entstehen. Lastschrift bleibt inkl. Inline-IBAN-Eingabe
    // möglich, solange freigegeben.
    const companyPaymentMethodsNotInBooker = companyPaymentMethods
        ? companyPaymentMethodKeys.filter(
              (key) =>
                  staffRestriction.isTypeSelectable(key as PaymentMethodType)
                  && !bookerPaymentMethodTypes?.includes(key as PaymentMethodType),
          )
        : [];

    const AvailablePaymentMethodsTable = () => {
        const [selectedRowKeys, setSelectedRowKeys] = useState<Array<PaymentMethodType>>([]);

        const [sepaDebitData, setSepaDebitData] = useState<SepaDebitIntput>();

        const [sepaDebit, setSepaDebit] = useState(false);

        useEffect(() => {
            if (selectedRowKeys.includes("sepaDebit")) {
                setSepaDebit(true);
            } else {
                setSepaDebit(false);
            }
        }, [selectedRowKeys]);

        // ADD NEW PAYMENTMETHOD TO BOOKER ---------------------------------------------------------------------------

        const [addPaymentMethodMutation] = useAddPaymentMethodMutation();

        const addPaymentMethod = async ({
            bookerId,
            selectedRowKeys,
            sepaDebitInput,
        }: {
            bookerId?: Booker["id"];
            selectedRowKeys?: Array<PaymentMethodType>;
            sepaDebitInput?: SepaDebitIntput;
        }) => {
            if (!bookerId || !selectedRowKeys || !courseId) {
                return;
            }

            const refetchQueries = [
                "GetAppSettings",
                {query: GetAppSettingsDocument},
                "GetBookedAttendeesByCourseId",
                {
                    query: GetBookedAttendeesByCourseIdDocument,
                    variables: {courseId},
                },
                "GetPaymentMethodsByBookerId",
                {
                    query: GetPaymentMethodsByBookerIdDocument,
                    variables: {bookerId},
                },
            ];

            if (bookingId) {
                refetchQueries.push({
                    query: GetBookingByIdDocument,
                    variables: {id: bookingId},
                });
                refetchQueries.push("GetBookingByIdWithCourseLessonBookings", {
                    query: GetBookingByIdWithCourseLessonBookingsDocument,
                    variables: {id: bookingId},
                });
            }

            try {
                const result = await addPaymentMethodMutation({
                    variables: {
                        bookerId,
                        paymentMethodType: selectedRowKeys[0],
                        sepaDebitInput,
                    },
                    refetchQueries,
                });

                if (result.data?.addPaymentMethod) {
                    message.success("Bezahlmethode wurde hinzugefügt");
                    setSelectedPayment(result.data.addPaymentMethod);
                    setModalOpen(false);
                }
            } catch (error) {
                console.log("error", error);
                message.error("Bezahlmethode konnte nicht hinzugefügt werden");
            }
        };

        // ------------------------------------------------------------------------------------------------------------

        const renderBadge = (paymentMethod: string) => {
            const paymentMethodType = paymentMethod as PaymentMethodType;
            const paymentProvider = getProviderForPaymentMethodType(paymentMethodType, companyPaymentMethods);

            return <PaymentMethodBadge type={paymentMethodType} paymentProvider={paymentProvider} />;
        };

        type TableType = {key: string; title: string; dataIndex: string};

        // Hint if is not company method -------------------------------------------------------------------------------
        const isCompanyMethod = (record: TableType) => {
            if (!companyPaymentMethods) return null;
            if (companyPaymentMethods[record.dataIndex as CompanyPaymentMethod]) {
                return null;
            }

            return (
                <Tooltip title={i18n.views.PaymentMethodTable.notCompanyMethod()}>
                    <ExclamationCircleOutlined style={{color: red[3], fontSize: "1.5em"}} />
                </Tooltip>
            );
        };

        const columns: Array<ColumnProps<{
            title: string;
            dataIndex: string;
        }>> = [
            {
                key: "companyPaymentMethod",
                title: "",
                dataIndex: "companyPaymentMethod",
                render: (text, record: any) => {
                    return isCompanyMethod(record);
                },
                width: 10,
            },
            {
                title: "Verfügbare Bezahlmethoden",
                dataIndex: "dataIndex",
                render: (text, record: any) => renderBadge(text),
            },
        ];

        const dataSource = Object.values(companyPaymentMethodsNotInBooker).map((method) => {
            return {
                key: method,
                title: method,
                dataIndex: method,
            };
        });

        const onSelectChange = (selectedRowKeys: Array<PaymentMethodType>) => {
            setSelectedRowKeys(selectedRowKeys);
        };

        const rowSelection = {
            selectedRowKeys,
            onChange: onSelectChange,
        };

        const expandedRowRender = (record: TableType) => {
            if (record.dataIndex === "sepaDebit" && sepaDebit) {
                return (
                    <SepaDebitPaymentMethodModal
                        asModal={false}
                        onSubmit={(data) => {
                            setSepaDebitData(data as SepaDebitIntput);
                            addPaymentMethod({
                                bookerId,
                                selectedRowKeys,
                                sepaDebitInput: data as SepaDebitIntput,
                            });
                        }}
                    />
                );
            }
        };

        const expandable: ExpandableConfig<TableType> = {
            // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
            rowExpandable(record) {
                return record.dataIndex === "sepaDebit";
            },
            expandedRowRender,
            expandedRowKeys: sepaDebit ? ["sepaDebit"] : [],
            showExpandColumn: false,
        };

        return (
            <Space direction="vertical" style={{width: "100%", paddingTop: 0, marginTop: 0}}>
                <Table<TableType>
                    columns={columns}
                    dataSource={dataSource}
                    pagination={false}
                    rowSelection={{type: "radio", ...rowSelection}}
                    expandable={expandable}
                    showHeader={false}
                />
                {!sepaDebit && (
                    <div
                        style={{
                            // width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            // border: "2px dotted red",
                        }}
                    >
                        <Button style={{width: "49%"}} danger onClick={() => setModalOpen(false)}>
                            {"Abbrechen"}
                        </Button>
                        <Button
                            style={{width: "49%"}}
                            type="primary"
                            disabled={selectedRowKeys.length === 0}
                            onClick={() => {
                                addPaymentMethod({bookerId, selectedRowKeys});
                            }}
                        >
                            {okButtonText ?? "Bezahlmethoden hinzufügen"}
                        </Button>
                    </div>
                )}
            </Space>
        );
    };

    return (
        <Space direction="vertical" style={{width: "100%"}}>
            <AvailablePaymentMethodsTable />
        </Space>
    );
};

export default memo(BookingAddPaymentMethod);
